import { Button, Group, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { adminApi } from '@/api/admin';
import { ApiError, errorMessage } from '@/api/http';
import { markOwnProgrammeWrite, useSetProgramme } from '@/api/queries';
import type { AdminProgramme, ProgrammeStatus } from '@/api/types';
import { useRole } from '@/auth/context';
import {
  atLeast,
  ROLE_LABEL,
  STATUS_LABEL,
  TRANSITIONS,
  type TransitionDef,
} from '@/auth/permissions';
import { ReasonTooltip } from '@/components/RoleTooltip';
import { formatDateTime, isFuture } from '@/lib/format';

/**
 * Workflow buttons for the current status and role (06 §3). Steps the role can never take are
 * hidden, except approve/publish which editors see disabled so the next step is clear.
 */
export function WorkflowBar({ programme, dirty }: { programme: AdminProgramme; dirty: boolean }) {
  const role = useRole();
  const setProgramme = useSetProgramme();
  const transition = useMutation({
    mutationFn: (to: ProgrammeStatus) => adminApi.transitionProgramme(programme.id, to),
    onSuccess: (p) => {
      markOwnProgrammeWrite(p.id);
      setProgramme(p);
      notifications.show({
        color: 'leaf',
        message: `Now ${STATUS_LABEL[p.status].toLowerCase()}.`,
      });
    },
    onError: (e) => {
      const issues =
        e instanceof ApiError && Array.isArray(e.problem.issues)
          ? (e.problem.issues as { message: string }[])
          : [];
      notifications.show({
        color: 'coral',
        title: 'Status not changed',
        message: issues.length
          ? `${errorMessage(e)} ${issues.map((i) => i.message).join(' ')}`
          : errorMessage(e),
      });
    },
  });

  const defs = TRANSITIONS[programme.status].filter(
    (d) => atLeast(role, d.minRole) || (d.primary && d.needsValidation),
  );
  if (defs.length === 0) return null;

  const run = (d: TransitionDef) => {
    const scheduledPublish = d.to === 'published' && isFuture(programme.publish_at);
    const message = scheduledPublish
      ? `It will appear in the app from ${formatDateTime(programme.publish_at)}.`
      : d.confirm;
    if (!message) {
      transition.mutate(d.to);
      return;
    }
    modals.openConfirmModal({
      title: `${labelFor(d, programme)}: ${programme.title}?`,
      children: <Text size="sm">{message}</Text>,
      labels: { confirm: labelFor(d, programme), cancel: 'Cancel' },
      confirmProps: { color: d.to === 'archived' ? 'coral' : undefined },
      onConfirm: () => transition.mutate(d.to),
    });
  };

  return (
    <Group gap="xs" role="group" aria-label="Workflow">
      {defs.map((d) => {
        const reason = !atLeast(role, d.minRole)
          ? `Needs the ${ROLE_LABEL[d.minRole].toLowerCase()} role.`
          : dirty
            ? 'Save or discard your changes first.'
            : d.needsValidation && programme.issues.length > 0
              ? `Fix ${programme.issues.length} issue${programme.issues.length === 1 ? '' : 's'} in the checklist first.`
              : null;
        return (
          <ReasonTooltip key={d.to} reason={reason}>
            <Button
              variant={d.primary ? 'filled' : 'default'}
              color={d.to === 'archived' ? 'coral' : undefined}
              disabled={!!reason}
              loading={transition.isPending && transition.variables === d.to}
              onClick={() => run(d)}
            >
              {labelFor(d, programme)}
            </Button>
          </ReasonTooltip>
        );
      })}
    </Group>
  );
}

function labelFor(d: TransitionDef, p: AdminProgramme) {
  return d.to === 'published' && isFuture(p.publish_at) ? 'Schedule publish' : d.label;
}
